import { Component, ElementRef, HostListener, Input, ViewChild } from '@angular/core';

import { Router } from '@angular/router';
import { IonButton, IonIcon, IonPopover } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  chatbubbleEllipsesOutline,
  fileTrayFullOutline,
  logOutOutline,
  settingsOutline,
} from 'ionicons/icons';
import { AuthService } from '../../services/auth.service';
import { PlatformModeService } from '../../services/platform-mode.service';
import { EnvironmentBadgeComponent } from '../environment-badge/environment-badge.component';
import { ThemeSelectorComponent } from '../theme-selector/theme-selector.component';

@Component({
  selector: 'app-user-menu',
  standalone: true,
  imports: [IonButton, IonIcon, IonPopover, EnvironmentBadgeComponent, ThemeSelectorComponent],
  templateUrl: './user-menu.component.html',
  styleUrls: ['./user-menu.component.scss']
})
export class UserMenuComponent {
  @Input() userName: string | null = null;
  @Input() userNickname: string | null = null;
  @Input() userEmail: string | null = null;
  @ViewChild('userPopover', { read: ElementRef }) userPopoverRef?: ElementRef<HTMLElement>;

  isOpen = false;
  popoverEvent: MouseEvent | undefined;

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly platformModeService: PlatformModeService
  ) {
    addIcons({
      settingsOutline,
      chatbubbleEllipsesOutline,
      fileTrayFullOutline,
      logOutOutline
    });
  }

  get displayName(): string {
    return this.userNickname || this.userName || 'Account';
  }

  get initials(): string {
    const parts = this.displayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase());

    return parts.length > 0 ? parts.join('') : 'A';
  }

  get isOwner(): boolean {
    return this.authService.currentUser?.platformRole === 'owner';
  }

  closeMenuImmediately(): void {
    this.isOpen = false;
    void this.dismissPopoverOverlay();
  }

  onTriggerClick(event: Event): void {
    event.stopPropagation();
    if (this.isOpen) {
      this.closeMenuImmediately();
      return;
    }

    this.openMenu(event);
  }

  async goToAccount(): Promise<void> {
    this.closeMenuImmediately();
    await this.dismissPopoverOverlay();
    const target = this.platformModeService.isCompactViewport() ? ['/account'] : ['/account/profile'];
    await this.router.navigate(target);
  }

  async goToFeedback(): Promise<void> {
    this.closeMenuImmediately();
    await this.dismissPopoverOverlay();
    await this.router.navigate(['/account/feedback']);
  }

  async goToFeedbackInbox(): Promise<void> {
    this.closeMenuImmediately();
    await this.dismissPopoverOverlay();
    await this.router.navigate(['/admin/tickets']);
  }

  async logout(): Promise<void> {
    this.closeMenuImmediately();
    await this.dismissPopoverOverlay();
    this.authService.logout().subscribe(() => {
      this.router.navigate(['/login']);
    });
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  @HostListener('document:click')
  handleDocumentClick(): void {
    this.closeMenuImmediately();
  }

  @HostListener('document:keydown.escape')
  handleEscapeKey(): void {
    this.closeMenuImmediately();
  }

  onPopoverDidDismiss(): void {
    this.closeMenuImmediately();
  }

  private openMenu(event: Event): void {
    this.setPopoverEventFrom(event);
    this.isOpen = true;
  }

  private async dismissPopoverOverlay(): Promise<void> {
    const popover = this.userPopoverRef?.nativeElement as
      | { dismiss?: () => Promise<boolean> }
      | undefined;

    if (!popover?.dismiss) {
      return;
    }

    try {
      await popover.dismiss();
    } catch {
      // Popover may already be dismissed.
    }
  }

  private setPopoverEventFrom(event: Event): void {
    if (event instanceof MouseEvent) {
      this.popoverEvent = event;
      return;
    }

    const target = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    if (!target) {
      return;
    }

    const rect = target.getBoundingClientRect();
    this.popoverEvent = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      clientX: rect.right,
      clientY: rect.bottom
    });
  }
}
